import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiRequest } from '@/lib/api-client';

import type { MemberConnectCodeResponse, ProfileScanOutcome } from './types';

/**
 * The profile connect code's whole HTTP surface (`MEM-057`, ADR 053).
 *
 * Every call needs a session and none carries a member id: the owner is the session, and the
 * member whose code was scanned is named by the code and by nothing else (`MEM-057` AC-7).
 *
 * **No `retry` on the scan**, for the reason the in-event scan gives: a scan that cannot reach the
 * service must fail visibly and write nothing, and a retry turns one tap into several writes while
 * holding the member's phone.
 */

const surfaceKey = ['member-connect-code', 'me'] as const;

/**
 * `MEM-057` AC-2, AC-6 — the member's own code and their undismissed notices.
 *
 * **`staleTime: Infinity`.** A profile code changes only when its owner replaces it, and that
 * path writes the new surface into this cache itself. Re-fetching on a window focus would put a
 * request on the wire to be told the same 43 characters — and, worse, would make the code the
 * member is holding up flicker through a skeleton while somebody is trying to scan it.
 *
 * The notices ride on the same payload and are therefore as stale as the code is. That is the
 * trade `member-in-event` refuses for its roster and this accepts for a much quieter list: a
 * notice that arrives on the next visit rather than within twenty seconds costs nothing, and
 * polling Your account would be a request a minute for a screen nobody watches.
 */
export function useMyConnectCode(
  options: { readonly enabled: boolean } = { enabled: true },
): UseQueryResult<MemberConnectCodeResponse> {
  return useQuery({
    queryKey: surfaceKey,
    queryFn: () => apiRequest<MemberConnectCodeResponse>('/api/member-connect-code/me'),
    enabled: options.enabled,
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * `MEM-057` AC-2 — replace the code; the old one stops resolving.
 *
 * The response **is** the new surface, and it is written straight into the cache rather than
 * invalidated. A member who has just revoked their code must not watch a skeleton where it was:
 * the gap would read as "it worked, and now there is nothing", which is the one impression a
 * revocation control must not give.
 */
export function useReplaceMyConnectCode(): UseMutationResult<MemberConnectCodeResponse, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<MemberConnectCodeResponse>('/api/member-connect-code/me/replace', {
        method: 'POST',
      }),
    retry: false,
    onSuccess: (response) => {
      queryClient.setQueryData(surfaceKey, response);
    },
  });
}

/**
 * `MEM-057` AC-3 — somebody scanned a profile code.
 *
 * One request, and the connection exists when it answers. The connections list is invalidated on
 * anything that created one, so walking to `/app/connections` afterwards shows the person who was
 * just scanned rather than a list that has not heard.
 */
export function useScanProfileCode(): UseMutationResult<ProfileScanOutcome, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      apiRequest<ProfileScanOutcome>('/api/member-connect-code/scans', {
        method: 'POST',
        body: { code },
      }),
    retry: false,
    onSuccess: async (outcome) => {
      if (outcome.result === 'refused') return;
      await queryClient.invalidateQueries({ queryKey: ['connections'] });
    },
  });
}

/** `MEM-057` AC-6 — mark one notice read. */
export function useDismissProfileScanNotice(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (connectionRef: string) =>
      apiRequest<void>(`/api/member-connect-code/notices/${encodeURIComponent(connectionRef)}`, {
        method: 'DELETE',
      }),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: surfaceKey });
    },
  });
}
